// src/pages/recommandations/ListeRecommandations.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Spinner, Modal, Button, Table, Pagination, Form, InputGroup } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import toast, { Toaster } from 'react-hot-toast';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

// ===== UTILITAIRES =====
const truncateText = (text, maxLength = 30) => {
  if (!text) return '-';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const formatDateSimple = (dateString) => {
  if (!dateString) return '-';
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('fr-FR', options);
};

const showToast = (message, type = 'success') => {
  const toastOptions = {
    duration: 3000,
    position: 'top-right',
    style: {
      borderRadius: '4px',
      background: '#fff',
      color: '#333',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      padding: '12px 16px',
      fontSize: '0.85rem',
      borderLeft: `4px solid ${type === 'success' ? COLORS.success : type === 'error' ? COLORS.danger : COLORS.warning}`
    },
    iconTheme: {
      primary: type === 'success' ? COLORS.success : type === 'error' ? COLORS.danger : COLORS.warning,
      secondary: '#fff',
    },
  };
  switch(type) {
    case 'success': toast.success(message, toastOptions); break;
    case 'error': toast.error(message, toastOptions); break;
    case 'warning': toast(message, { ...toastOptions, icon: '⚠️' }); break;
    default: toast(message, toastOptions);
  }
};

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }
    
  

   .bg-warning { border-left-color: #f59e0b; }
  .bg-success { border-left-color: #10b981;}
  .bg-danger { border-left-color: #ef4444; }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  /* Cartes */
  .card-modern {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border-top: 3px solid transparent;
    transition: box-shadow 0.2s;
  }
  .card-modern:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
  .card-modern.border-primary {
    border-top-color: ${COLORS.primary};
  }
  .card-modern.border-secondary {
    border-top-color: ${COLORS.secondary};
  }

  .card-header-modern {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.8rem 1.2rem;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .card-header-modern i {
    color: ${COLORS.secondary};
  }

  .card-body-modern {
    padding: 1.2rem;
  }

  /* KPI Cards (comme ListeMembres) */
  .kpi-card {
    background: white;
    border-radius: 0px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    transition: all 0.2s ease;
    height: 100%;
    border-left: 2px solid;
  }
  .kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }

  .en-attente { background: #f59e0b; }
        .acceptee { background: #10b981; }
        .refusee { background: #ef4444; }


  .kpi-primary { border-left-color: ${COLORS.primary}; }
  .kpi-warning { border-left-color: #f59e0b; }
  .kpi-success { border-left-color: #10b981;}
  .kpi-danger { border-left-color: #ef4444; }

  .kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .kpi-primary .kpi-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .kpi-warning .kpi-icon { background: rgba(245, 158, 11, 0.08); color: #f59e0b; }
  .kpi-success .kpi-icon { background: rgba(16, 185, 129, 0.08); color: #10b981; }
  .kpi-danger .kpi-icon { background: rgba(239, 68, 68, 0.08); color: #ef4444; }

  .kpi-content { flex: 1; }
  .kpi-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6c757d;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  .kpi-value {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0.1rem 0 0 0;
    color: #1e293b;
  }

  /* Filtres */
  .filter-card {
    border: none;
    border-radius: 4px;
    background: ${COLORS.primary};
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .filter-card .form-control,
  .filter-card .form-select {
    border-radius: 4px;
    border: none;
    padding: 0.35rem 0.7rem;
    font-size: 0.8rem;
    background: rgba(255,255,255,0.95);
  }
  .filter-card .form-control:focus,
  .filter-card .form-select:focus {
    box-shadow: 0 0 0 3px rgba(255,255,255,0.2);
  }

  /* Tableau */
  .table-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    overflow: hidden;
  }

  .table-header-secondary {
    background: ${COLORS.secondary};
    color: white;
    padding: 0.6rem 1rem;
  }

  .table-modern thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }

  .table-modern td {
    white-space: nowrap;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .table-modern tbody tr {
    transition: all 0.2s ease;
  }
  .table-modern tbody tr:hover {
    background: #f8fafc;
  }

  .reference-badge {
    background: #f1f4f9;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-family: monospace;
    color: #334155;
  }

  .badge-statut {
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.65rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: white;
  }
  .badge-statut i {
    font-size: 0.7rem;
  }
  .badge-statut-en-attente { background: ${COLORS.warning}; }
  .badge-statut-acceptee { background: ${COLORS.success}; }
  .badge-statut-refusee { background: ${COLORS.danger}; }

  /* Actions */
  .action-btn {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    transition: all 0.2s ease;
    font-size: 0.9rem;
  }
  .action-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0,0,0,0.08);
  }
  .action-btn-view { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .action-btn-view:hover { background: ${COLORS.primary}; color: white; }
  .action-btn-edit { background: ${COLORS.secondary}15; color: ${COLORS.secondary}; }
  .action-btn-edit:hover { background: ${COLORS.secondary}; color: white; }
  .action-btn-delete { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .action-btn-delete:hover { background: ${COLORS.danger}; color: white; }
  .action-btn-accept { background: ${COLORS.success}15; color: ${COLORS.success}; }
  .action-btn-accept:hover { background: ${COLORS.success}; color: white; }
  .action-btn-refuse { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .action-btn-refuse:hover { background: ${COLORS.danger}; color: white; }

  /* Pagination */
  .pagination-modern .page-link {
    border: none;
    margin: 0 2px;
    border-radius: 4px;
    color: #64748b;
    font-size: 0.8rem;
    padding: 0.3rem 0.7rem;
    transition: all 0.2s ease;
  }
  .pagination-modern .page-link:hover {
    background: ${COLORS.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(41,90,167,0.2);
  }
  .pagination-modern .page-item.active .page-link {
    background: ${COLORS.primary};
    color: white;
    box-shadow: 0 4px 8px rgba(41,90,167,0.3);
  }

  /* Loading */
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Modal suppression */
  .delete-modal .modal-content {
    border: none;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
  .delete-modal .modal-header {
    border-bottom: none;
    padding: 1.2rem 1.5rem 0.5rem;
  }
  .delete-modal .modal-footer {
    border-top: none;
    padding: 0.5rem 1.5rem 1.2rem;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
    .table-modern td {
      max-width: 80px;
    }
  }
`;

export default function ListeRecommandations() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [egliseFromFilter, setEgliseFromFilter] = useState('');
  const [egliseToFilter, setEgliseToFilter] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [eglises, setEglises] = useState([]);
  const [membres, setMembres] = useState([]);
  const [stats, setStats] = useState({ total: 0, enAttente: 0, acceptees: 0, refusees: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recommandationToDelete, setRecommandationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ===== FETCH =====
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reload')) {
      setRefreshTrigger(prev => prev + 1);
      navigate('/recommandations/liste', { replace: true });
    }
  }, [location, navigate]);

  const fetchEglises = useCallback(async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&type=siege&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) setEglises(result.eglises || []);
    } catch (err) { console.error(err); showToast('Erreur chargement églises', 'error'); }
  }, []);

  const fetchMembres = useCallback(async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/membres.php?user_id=${userId}&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) setMembres(result.membres || []);
    } catch (err) { console.error(err); showToast('Erreur chargement membres', 'error'); }
  }, []);

  const fetchRecommandations = useCallback(async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/pages/signin2'); return; }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const timestamp = new Date().getTime();

      let url = `https://josephsbusiness.com/api/recommandations.php?user_id=${userId}&page=${currentPage}&limit=${itemsPerPage}&_=${timestamp}`;
      if (user.role !== 'Super Administrateur') {
        url += `&eglise_from=${user.matri_eglise}&eglise_to=${user.matri_eglise}`;
      } else {
        if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
        if (egliseFromFilter) url += `&eglise_from=${encodeURIComponent(egliseFromFilter)}`;
        if (egliseToFilter) url += `&eglise_to=${encodeURIComponent(egliseToFilter)}`;
      }
      if (statutFilter) url += `&statut=${encodeURIComponent(statutFilter)}`;

      const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();

      if (result.success) {
        setData(result);
        const recs = result.recommandations || [];
        let enAttente = 0, acceptees = 0, refusees = 0;
        recs.forEach(r => {
          if (r.statut === 'en_attente') enAttente++;
          else if (r.statut === 'acceptee') acceptees++;
          else if (r.statut === 'refusee') refusees++;
        });
        setStats({ total: result.pagination?.total || 0, enAttente, acceptees, refusees });
      } else {
        setError(result.message || 'Erreur de chargement');
        showToast(result.message || 'Erreur', 'error');
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, egliseFromFilter, egliseToFilter, statutFilter, navigate]);

  useEffect(() => {
    fetchEglises();
    fetchMembres();
  }, [fetchEglises, fetchMembres]);

  useEffect(() => {
    fetchRecommandations();
  }, [fetchRecommandations, refreshTrigger]);

  // ===== HANDLERS =====
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setRefreshTrigger(prev => prev + 1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setRefreshTrigger(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (rec) => {
    setRecommandationToDelete(rec);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recommandationToDelete) return;
    setDeleting(true);
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/recommandations.php?user_id=${userId}&id=${recommandationToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) {
        setShowDeleteModal(false);
        setRecommandationToDelete(null);
        setRefreshTrigger(prev => prev + 1);
        showToast('Recommandation supprimée', 'success');
      } else {
        showToast(result.message || 'Erreur suppression', 'error');
      }
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleAccepter = async (id) => {
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/recommandations.php?user_id=${userId}&id=${id}&action=accepter`, {
        method: 'PUT',
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) {
        setRefreshTrigger(prev => prev + 1);
        showToast('Recommandation acceptée', 'success');
      } else {
        showToast(result.message || 'Erreur', 'error');
      }
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const handleRefuser = async (id) => {
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/recommandations.php?user_id=${userId}&id=${id}&action=refuser`, {
        method: 'PUT',
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) {
        setRefreshTrigger(prev => prev + 1);
        showToast('Recommandation refusée', 'warning');
      } else {
        showToast(result.message || 'Erreur', 'error');
      }
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const getEgliseName = (matricule) => {
    const eglise = eglises.find(e => e.matricule === matricule);
    return eglise ? eglise.nom : matricule;
  };

  const getMembreInfo = (matricule) => {
    const membre = membres.find(m => m.matricule === matricule);
    return membre ? `${membre.nom} ${membre.prenom || ''}`.trim() : matricule;
  };

  const getStatutStyle = (statut) => {
    switch(statut) {
      case 'en_attente': return { label: 'En attente', className: 'bg-warning', icon: 'ri-time-line' };
      case 'acceptee':   return { label: 'Acceptée',   className: 'bg-success',   icon: 'ri-checkbox-circle-line' };
      case 'refusee':    return { label: 'Refusée',    className: 'bg-danger',    icon: 'ri-close-circle-line' };
      default:           return { label: statut,       className: '',                         icon: 'ri-question-line' };
    }
  };

  const exportToExcel = () => {
    if (!data?.recommandations) { showToast('Aucune donnée', 'warning'); return; }
    const fileName = `recommandations_${new Date().toISOString().split('T')[0]}.xlsx`;
    const exportData = data.recommandations.map(item => ({
      'Matricule': item.matricule,
      'Membre': getMembreInfo(item.matri_membre),
      'Église d\'origine': getEgliseName(item.matri_eglise_from),
      'Église de destination': getEgliseName(item.matri_eglise_to),
      'Période': item.periode || '-',
      'Description': item.description || '-',
      'Statut': getStatutStyle(item.statut).label,
      'Date création': formatDateSimple(item.created_at)
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, 'Recommandations');
    XLSX.writeFile(wb, fileName);
    showToast('Export Excel réussi', 'success');
  };

const printPDF = () => {
    // Récupérer les données
    const recommandations = data?.recommandations || [];

    // --- Récupération du logo et du nom depuis l'utilisateur ---
    let logoUrl = '../assets/img/logo.jpeg'; // fallback local
    let companyName = 'JOSEPHSBUSINESS';

    try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            if (user && user.temple) {
                companyName = user.temple;
            }
            if (user && user.logo_parent) {
                // URL absolue vers le logo (comme dans la sidebar)
                logoUrl = `https://josephsbusiness.com/assets/images/${user.logo_parent}`;
            }
        }
    } catch(e) {}

    // Informations société par défaut
    const company = {
        name: companyName,
        address: 'Libreville, Gabon',
        phone: '+241 00 00 00 00',
        email: 'contact@josephsbusiness.com'
    };

    // Générer les lignes du tableau
    const rows = recommandations.map(rec => {
        const statut = getStatutStyle(rec.statut);
        const cls = rec.statut === 'en_attente' ? '#f59e0b' : 
                    rec.statut === 'acceptee' ? '#10b981' : '#ef4444';
        return `
            <tr>
                <td>${getMembreInfo(rec.matri_membre)}</td>
                <td>${getEgliseName(rec.matri_eglise_from)}</td>
                <td>${getEgliseName(rec.matri_eglise_to)}</td>
                <td>${rec.periode || '-'}</td>
                <td>${rec.description ? truncateText(rec.description, 30) : '-'}</td>
                <td><span class="badge" style="background:${cls}">${statut.label}</span></td>
                <td>${formatDateSimple(rec.created_at)}</td>
            </tr>
        `;
    }).join('');

    const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Liste des recommandations</title>
            <meta charset="UTF-8">
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1e293b; background: white; }
                .header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 3px solid ${COLORS.primary};
                    padding-bottom: 15px;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                }
                .header-left { display: flex; align-items: center; gap: 15px; }
                .logo { height: 60px; width: auto; }
                .header-title h1 { font-size: 22px; color: ${COLORS.primary}; margin: 0; }
                .header-title .subtitle { color: #64748b; font-size: 13px; margin: 3px 0 0; }
                .header-right { text-align: right; font-size: 13px; color: #64748b; }
                .company-name { font-size: 18px; font-weight: 600; color: ${COLORS.primary}; }
                .summary {
                    background: #f8fafc;
                    padding: 12px 20px;
                    border-left: 4px solid ${COLORS.secondary};
                    margin-bottom: 20px;
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: space-around;
                    gap: 10px;
                }
                .summary-item { font-size: 14px; }
                .summary-item strong { color: ${COLORS.primary}; }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                    margin-top: 15px;
                }
                th {
                    background: ${COLORS.primary};
                    color: white;
                    padding: 10px 8px;
                    text-align: left;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                td {
                    padding: 8px;
                    border-bottom: 1px solid #e2e8f0;
                }
                tr:nth-child(even) { background: #f8fafc; }
                .badge {
                    display: inline-block;
                    padding: 3px 12px;
                    border-radius: 4px;
                    color: white;
                    font-weight: 600;
                    font-size: 11px;
                }
                .footer {
                    margin-top: 25px;
                    text-align: center;
                    font-size: 12px;
                    color: #94a3b8;
                    border-top: 1px solid #e2e8f0;
                    padding-top: 15px;
                }
                @media print {
                    body { padding: 20px; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <div class="header-left">
                    <img src="${logoUrl}" alt="Logo" class="logo" onerror="this.style.display='none'">
                    <div>
                        <div class="company-name">${company.name}</div>
                        <div class="subtitle">Liste des recommandations</div>
                    </div>
                </div>
                <div class="header-right">
                    <div><strong>Total</strong> : ${stats.total}</div>
                    <div style="font-size:12px;">${new Date().toLocaleDateString('fr-FR')}</div>
                </div>
            </div>

            <div class="summary">
                <span class="summary-item"><strong>Total</strong> : ${stats.total}</span>
                <span class="summary-item"><strong>En attente</strong> : ${stats.enAttente}</span>
                <span class="summary-item"><strong>Acceptées</strong> : ${stats.acceptees}</span>
                <span class="summary-item"><strong>Refusées</strong> : ${stats.refusees}</span>
            </div>

            <table>
                <thead>
                    <tr>
                        <th>Membre</th>
                        <th>Origine</th>
                        <th>Destination</th>
                        <th>Période</th>
                        <th>Description</th>
                        <th>Statut</th>
                        <th>Date</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                </tbody>
            </table>

            <div class="footer">
                Document généré automatiquement • ${new Date().toLocaleDateString('fr-FR')}
            </div>
        </body>
        </html>
    `;

    // Créer un iframe invisible
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    iframe.contentWindow.onload = () => {
        iframe.contentWindow.print();
        setTimeout(() => {
            if (document.body.contains(iframe)) document.body.removeChild(iframe);
        }, 1000);
    };
};

  if (loading && !data) {
    return (
      <>
        <Header /><Toaster />
        <div className="main main-app p-3 p-lg-4 dashboard-styled">
          <div className="loading-wrapper">
            <div className="text-center">
              <div className="loading-ring-wrapper mb-3">
                <div className="loading-ring"></div>
                <div className="loading-ring-inner"></div>
              </div>
              <h5 className="fw-bold" style={{ color: COLORS.primaryDark }}>Chargement...</h5>
              <p className="text-muted small">Récupération des recommandations</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const recommandations = data?.recommandations || [];
  const pagination = data?.pagination || { total: 0, pages: 1 };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(pagination.pages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
      if (start > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }
    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }
    if (end < pagination.pages) {
      if (end < pagination.pages - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      items.push(<Pagination.Item key={pagination.pages} onClick={() => handlePageChange(pagination.pages)}>{pagination.pages}</Pagination.Item>);
    }
    return items;
  };

  return (
    <>
      <style>{styles}</style>
      <Header />
      <Toaster />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* En‑tête */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-mail-send-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Recommandations</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern"><i className="ri-bar-chart-line"></i> {stats.total}</span>
                  <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                    <i className="ri-time-line"></i> {stats.enAttente} en attente
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                    <i className="ri-checkbox-circle-line"></i> {stats.acceptees} acceptées
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                    <i className="ri-close-circle-line"></i> {stats.refusees} refusées
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button size="sm" className="btn-modern-outline" onClick={() => setShowFilters(!showFilters)}>
                <i className="ri-filter-line"></i> {showFilters ? 'Masquer' : 'Filtres'}
              </Button>
              <Button size="sm" className="btn-modern-primary" onClick={exportToExcel}>
                <i className="ri-file-excel-line"></i> Excel
              </Button>
              <Button size="sm" className="btn-modern-primary" style={{ background: COLORS.secondary, color: '#1e293b' }} onClick={printPDF}>
                <i className="ri-file-pdf-line"></i> PDF
              </Button>
              <Button size="sm" className="btn-modern-primary" onClick={() => navigate('/recommandations/nouveau')}>
                <i className="ri-add-line"></i> Nouveau
              </Button>
            </div>
          </div>

          {/* Filtres */}
          {showFilters && (
            <Card className="filter-card mb-4 fade-in">
              <Card.Body className="p-3">
                <Form onSubmit={handleSearch}>
                  <Row className="g-2">
                    <Col md={3}>
                      <InputGroup size="sm">
                        <InputGroup.Text style={{ background: 'transparent', border: 'none', color: 'white' }}>
                          <i className="ri-search-line"></i>
                        </InputGroup.Text>
                        <Form.Control
                          placeholder="Rechercher..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={2}>
                      <Form.Select value={egliseFromFilter} onChange={(e) => setEgliseFromFilter(e.target.value)}>
                        <option value="">Église origine</option>
                        {eglises.map(eg => <option key={eg.matricule} value={eg.matricule}>{truncateText(eg.nom, 20)}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Select value={egliseToFilter} onChange={(e) => setEgliseToFilter(e.target.value)}>
                        <option value="">Église destination</option>
                        {eglises.map(eg => <option key={eg.matricule} value={eg.matricule}>{truncateText(eg.nom, 20)}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Select value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
                        <option value="">Statut</option>
                        <option value="en_attente">En attente</option>
                        <option value="acceptee">Acceptée</option>
                        <option value="refusee">Refusée</option>
                      </Form.Select>
                    </Col>
                    <Col md={3}>
                      <Button type="submit" size="sm" className="btn-modern-primary w-100">
                        <i className="ri-filter-3-line me-1"></i> Filtrer
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* Statistiques en KPI */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-mail-send-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Total</span>
                  <h3 className="kpi-value">{stats.total}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-warning">
                <div className="kpi-icon"><i className="ri-time-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">En attente</span>
                  <h3 className="kpi-value">{stats.enAttente}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-success">
                <div className="kpi-icon"><i className="ri-checkbox-circle-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Acceptées</span>
                  <h3 className="kpi-value">{stats.acceptees}</h3>
                </div>
              </div>
            </Col>
            <Col xs={6} md={3}>
              <div className="kpi-card kpi-danger">
                <div className="kpi-icon"><i className="ri-close-circle-line"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Refusées</span>
                  <h3 className="kpi-value">{stats.refusees}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* Tableau */}
          <Card className="table-card">
            <div className="table-header-secondary d-flex justify-content-between align-items-center px-3 py-2">
              <span className="fw-semibold"><i className="ri-list-check me-1"></i> Liste des recommandations</span>
              <div className="d-flex align-items-center gap-2">
                <small className="text-white-50">Lignes</small>
                <Form.Select size="sm" style={{ width: '65px', background: 'rgba(255,255,255,0.9)', border: 'none' }} value={itemsPerPage} onChange={handleItemsPerPageChange}>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </Form.Select>
              </div>
            </div>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle table-modern">
                  <thead>
                    <tr>
                      <th className="px-2">Matricule</th>
                      <th className="px-2">Membre</th>
                      <th className="px-2">Église origine</th>
                      <th className="px-2">Église destination</th>
                      <th className="px-2">Période</th>
                      <th className="px-2">Statut</th>
                      <th className="px-2 text-center" style={{ width: '160px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recommandations.length > 0 ? (
                      recommandations.map(rec => {
                        const statut = getStatutStyle(rec.statut);
                        return (
                          <tr key={rec.id}>
                            <td className="px-2"><span className="reference-badge">{rec.matricule}</span></td>
                            <td className="px-2 text-bold" style={{fontWeight:'bold'}}>{truncateText(getMembreInfo(rec.matri_membre), 20)}</td>
                            <td className="px-2">{truncateText(getEgliseName(rec.matri_eglise_from), 20)}</td>
                            <td className="px-2">{truncateText(getEgliseName(rec.matri_eglise_to), 20)}</td>
                            <td className="px-2">{rec.periode || '-'}</td>
                            <td className="px-2">
                              <span className={`badge-statut ${statut.className}`}>
                                <i className={statut.icon}></i> {statut.label}
                              </span>
                            </td>
                            <td className="px-2 text-center">
                              <div className="d-flex gap-1 justify-content-center">
                                <button className="action-btn action-btn-view" title="Voir" onClick={() => navigate(`/recommandations/details/${rec.id}`)}>
                                  <i className="ri-eye-line"></i>
                                </button>
                                {rec.statut === 'en_attente' && (
                                  <>
                                    <button className="action-btn action-btn-accept" title="Accepter" onClick={() => handleAccepter(rec.id)}>
                                      <i className="ri-check-line"></i>
                                    </button>
                                    <button className="action-btn action-btn-refuse" title="Refuser" onClick={() => handleRefuser(rec.id)}>
                                      <i className="ri-close-line"></i>
                                    </button>
                                  </>
                                )}
                                <button className="action-btn action-btn-edit" title="Modifier" onClick={() => navigate(`/recommandations/modifier/${rec.id}`)}>
                                  <i className="ri-edit-line"></i>
                                </button>
                                <button className="action-btn action-btn-delete" title="Supprimer" onClick={() => handleDeleteClick(rec)}>
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr><td colSpan="7" className="text-center py-4">
                        <i className="ri-inbox-line" style={{ fontSize: '2rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}></i>
                        <p className="text-muted small">Aucune recommandation</p>
                      </td></tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            {pagination.pages > 1 && (
              <Card.Footer className="border-0 bg-transparent pt-2 pb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">Page {currentPage} / {pagination.pages}</small>
                  <Pagination className="pagination-modern mb-0">
                    <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                    {renderPaginationItems()}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
                    <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={currentPage === pagination.pages} />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>

          <Footer />
        </div>
      </div>

      {/* Modal de suppression */}
     <Modal 
  show={showDeleteModal} 
  onHide={() => setShowDeleteModal(false)} 
  centered 
  className="delete-modal"
>
  <Modal.Header closeButton className="border-0">
    <Modal.Title className="d-flex align-items-center gap-2">
      <div className="delete-icon-wrapper" style={{
        width: '40px',
        height: '40px',
        borderRadius: '4px',
        background: `${COLORS.danger}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <i className="ri-delete-bin-line" style={{ fontSize: '1.4rem', color: COLORS.danger }}></i>
      </div>
      <div>
        <h5 className="fw-bold mb-0" style={{ color: COLORS.primary }}>Confirmer la suppression</h5>
        <p className="text-muted small mb-0">Cette action est irréversible</p>
      </div>
    </Modal.Title>
  </Modal.Header>

  <Modal.Body>
    {recommandationToDelete && (
      <>
        <p className="text-center mb-3">
          Voulez-vous supprimer cette recommandation ?
        </p>

        {/* Carte d'information */}
        <div className="p-3" style={{
          background: '#f8fafc',
          borderRadius: '4px',
          borderLeft: `3px solid ${COLORS.primary}`
        }}>
          <div className="d-flex flex-column gap-2">
            <div><strong>Membre :</strong> {getMembreInfo(recommandationToDelete.matri_membre)}</div>
            <div><strong>Église d'origine :</strong> {getEgliseName(recommandationToDelete.matri_eglise_from)}</div>
            <div><strong>Église destination :</strong> {getEgliseName(recommandationToDelete.matri_eglise_to)}</div>
            <div>
              <strong>Statut :</strong>{' '}
              <span className="badge" style={{
                background: getStatutStyle(recommandationToDelete.statut).color,
                color: 'white',
                borderRadius: '4px',
                padding: '0.25rem 0.6rem'
              }}>
                {getStatutStyle(recommandationToDelete.statut).label}
              </span>
            </div>
          </div>
        </div>

        {/* Avertissement */}
        <div className="mt-3 p-2" style={{
          background: `${COLORS.danger}10`,
          borderRadius: '4px',
          borderLeft: `3px solid ${COLORS.danger}`
        }}>
          <div className="d-flex align-items-center gap-2">
            <i className="ri-alert-line" style={{ color: COLORS.danger }}></i>
            <span className="small text-danger">
              <strong>Attention :</strong> Cette action est irréversible. Toutes les données associées seront supprimées.
            </span>
          </div>
        </div>
      </>
    )}
  </Modal.Body>

  <Modal.Footer className="border-0 pt-0">
    <Button
      variant="light"
      onClick={() => setShowDeleteModal(false)}
      disabled={deleting}
      className="btn-modern-outline"
    >
      Annuler
    </Button>
    <Button
      variant="danger"
      onClick={handleDeleteConfirm}
      disabled={deleting}
      className="btn-modern-primary"
      style={{ background: COLORS.danger }}
    >
      {deleting ? (
        <><Spinner size="sm" className="me-1" /> Suppression...</>
      ) : (
        <><i className="ri-delete-bin-line me-1"></i> Supprimer</>
      )}
    </Button>
  </Modal.Footer>
</Modal>
    </>
  );
}